import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { IndustryTemplate } from '../../../types/industryTemplates';
import { getAllTemplates } from '../../../services/industryTemplateService';

const TemplatesTab: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<IndustryTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');

  useEffect(() => {
    const run = async () => {
      try {
        setIsLoading(true);
        const list = await getAllTemplates();
        setTemplates(list);
      } catch (e: any) {
        console.error('Failed to load templates:', e);
        toast.error(e?.message || 'Failed to load templates');
      } finally {
        setIsLoading(false);
      }
    };
    void run();
  }, []);

  const industries = useMemo(() => {
    const categories = Array.from(new Set(templates.map(t => t.category)));
    return ['all', ...categories];
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    if (selectedIndustry === 'all') return templates;
    return templates.filter(t => t.category === selectedIndustry);
  }, [selectedIndustry, templates]);

  const handleSelectTemplate = (templateId: string) => {
    navigate('/business-plan/new', { state: { templateId } });
  };

  return (
    <div>
      {/* Industry Filter */}
      <div className="mb-6 flex flex-wrap gap-2">
        {industries.map(industry => (
          <button
            key={industry}
            onClick={() => setSelectedIndustry(industry)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              selectedIndustry === industry
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {industry === 'all' ? 'All Industries' : industry}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Loading templates...</div>
      ) : (
        <>
          {/* Templates Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                className="group relative rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md"
              >
                {/* Icon & Category */}
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-50 to-purple-50 text-2xl">
                    {template.icon}
                  </div>
                  <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                    {template.category}
                  </span>
                </div>

                {/* Template Name */}
                <h3 className="mb-2 font-semibold text-gray-900">
                  {template.name}
                </h3>

                {/* Description */}
                <p className="mb-4 text-sm text-gray-600 line-clamp-3">
                  {template.description}
                </p>

                {/* Highlights */}
                {template.keyMetrics?.length ? (
                  <ul className="mb-4 space-y-1">
                    {template.keyMetrics.slice(0, 3).map((metric, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-gray-500">
                        <svg className="h-3.5 w-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{metric}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {/* Use Template Button */}
                <button
                  onClick={() => handleSelectTemplate(template.id)}
                  className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                >
                  Use This Template
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TemplatesTab;
